import BannerLimit from '@/components/BannerLimit';
import BaseModal from '@/components/BaseModal';
import Layout from '@/components/layout';
import { optionLanguage } from '@/constants';
import useLimitPlan from '@/hooks/useLimitPlan';
import userPlans from '@/hooks/userPlans';
import { apiCaller } from '@/redux/query';
import { showBannerLimitTranslationSelector } from '@/redux/slice/banner.slice';
import { Banner, BlockStack, Box, Button, Card, Icon, InlineGrid, Select, Text } from '@shopify/polaris';
import { LanguageTranslateIcon } from '@shopify/polaris-icons';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import TranslationList from './components/Table';
import { TranslationStyled } from './styled';
import { checkShowErrorInline, findLanguage, handleToastMutation } from '@/helpers';
import { handleToast } from '@/redux/slice/Toast/toast.slice';
import { useAppDispatch } from '@/redux/store';
function Translation() {
  const { t } = useTranslation('translation');
  const { t: tSetting } = useTranslation('setting');
  const { isUserFree, isUserFreeOrBasic } = userPlans();
  const { numberRuleTranslation } = useLimitPlan();
  const dispatch = useAppDispatch();
  const [updateShopSetting, { isLoading }] = apiCaller.useUpdateAppSettingsMutation();
  const { data: languageUsed } = apiCaller.useGetAllLanguageUsedQuery();
  const isShowBannerLimitTranslation = useSelector(showBannerLimitTranslationSelector);
  const [isShowSelectLanguage, setIsShowSelectLanguage] = useState(false);

  const generalDetailQuerySettings = apiCaller.useGetGeneralDetailQuery();

  const defaultLanguage = useMemo(() => {
    return findLanguage(generalDetailQuerySettings.data?.data?.defaultLanguage || '');
  }, [generalDetailQuerySettings.data?.data?.defaultLanguage]);
  const shouldShowBannerLimitTranslation = isShowBannerLimitTranslation && isUserFreeOrBasic;

  const [localeSelected, setLocaleSelected] = useState(defaultLanguage);

  useEffect(() => {
    setLocaleSelected(defaultLanguage);
  }, [defaultLanguage]);

  const handleUpdateGeneralSetting = async () => {
    const res = await updateShopSetting({
      defaultLanguage: localeSelected?.value,
    });
    setIsShowSelectLanguage(false);
    const resToast = handleToastMutation(res, tSetting);

    if ('data' in res) {
      dispatch(handleToast({ ...resToast, content: tSetting('text_updated') }));
      return;
    } else {
      const isError = checkShowErrorInline(res);
      if (isError.statusCode) {
        dispatch(handleToast(handleToastMutation(isError.message, tSetting)));
      }
    }
  };

  const handleClickCloseModal = () => {
    setIsShowSelectLanguage(false);
    setLocaleSelected(defaultLanguage);
  };

  const handleChangeLocale = (value: string) => {
    setLocaleSelected(optionLanguage.find((item) => item.value === value));
  };

  const isVisibleBannerWarningDefaultLanguage = useMemo(() => {
    return languageUsed?.data?.includes(localeSelected?.value!);
  }, [languageUsed?.data, localeSelected?.value]);

  return (
    <Layout title={t('translation')} subtitle={t('translate_widgets_multiple_languages')}>
      <TranslationStyled>
        <BannerLimit isOpenBanner={shouldShowBannerLimitTranslation} keyBanner="showBannerLimitTranslation">
          {isUserFree ? t('upgrade_unlock_translation_options') : t('current_plan_language_limit', { numberRuleTranslation })}
        </BannerLimit>

        <Card>
          <InlineGrid columns={'auto 1fr'} gap={'050'}>
            <Icon source={LanguageTranslateIcon} />
            <Text as="h2" variant="headingSm">
              {t('default_language')}: {defaultLanguage?.label}
            </Text>
          </InlineGrid>
          <Text as="h2" variant="bodySm">
            {t('all_widgets_identified')}
          </Text>
          <Box paddingBlockStart={'200'}>
            <Button
              onClick={() => {
                setIsShowSelectLanguage(true);
              }}
            >
              {t('change_default')}
            </Button>
          </Box>
        </Card>
        <TranslationList />
      </TranslationStyled>

      <BaseModal
        open={isShowSelectLanguage}
        title="Select default language"
        onClose={handleClickCloseModal}
        primaryAction={{ content: 'Save', onAction: handleUpdateGeneralSetting, loading: isLoading }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: handleClickCloseModal,
            loading: isLoading,
          },
        ]}
      >
        <BlockStack gap={'200'}>
          <Text variant="headingSm" as="h3">
            {t('select_a_language')}
          </Text>
          <Select
            options={optionLanguage}
            label=""
            value={localeSelected?.value}
            onChange={handleChangeLocale}
            placeholder={t('common:select')}
          />
        </BlockStack>
        {isVisibleBannerWarningDefaultLanguage && (
          <div className="mt-16">
            <Banner tone="critical">
              You already have <b>{findLanguage(localeSelected?.value || '')?.label || ''}</b> version. Saving default language as{' '}
              <b>{findLanguage(localeSelected?.value || '')?.label || ''}</b> will immediately make current version{' '}
              <b>Inactive</b>.
            </Banner>
          </div>
        )}
      </BaseModal>
    </Layout>
  );
}

export default Translation;
